import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Home as HomeIcon, Building2, Factory, Landmark, Route as RouteIcon, Hammer, Sofa, PenTool, ShieldCheck, Clock, FileCheck2, HardHat, Quote } from "lucide-react";
import api from "@/lib/api";
import { Overline, SectionHead, FadeIn, CountUp, StatusBadge } from "@/components/site/Shared";

const HERO_IMG = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const OVERVIEW_IMG = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const coreServices = [
  { icon: HomeIcon, title: "Residential Construction", slug: "residential-construction", desc: "Villas, apartments and gated communities built to last generations." },
  { icon: Building2, title: "Commercial Construction", slug: "commercial-construction", desc: "Grade-A offices, retail and hospitality on fast-track schedules." },
  { icon: Factory, title: "Industrial Construction", slug: "industrial-construction", desc: "PEB warehouses, plants and logistics hubs engineered for uptime." },
  { icon: Landmark, title: "Government Projects", slug: "government-projects", desc: "Public works delivered with audit-ready discipline." },
  { icon: RouteIcon, title: "Roads & Infrastructure", slug: "roads-infrastructure", desc: "Corridors, drains and utilities built with calibrated precision." },
  { icon: Hammer, title: "Renovation & Remodeling", slug: "renovation-remodeling", desc: "Structural retrofits and makeovers for existing assets." },
  { icon: Sofa, title: "Interior Works", slug: "interior-works", desc: "Turnkey fit-outs with factory-finished joinery." },
  { icon: PenTool, title: "Architectural Design", slug: "architectural-design", desc: "Concept to GFC drawings with photorealistic 3D." },
];

const whyUs = [
  { icon: FileCheck2, title: "Milestone-Based Billing", desc: "You pay only for verified progress. Every invoice is backed by joint measurement and site photographs — never estimates." },
  { icon: ShieldCheck, title: "Uncompromised Quality", desc: "NABL-tested materials, cube tests every pour, and third-party audits. Our quality file is open to every client, always." },
  { icon: Clock, title: "98% On-Time Delivery", desc: "Monsoons, approvals and supply chains are planned into the baseline — not discovered later. Deadlines here are engineering commitments." },
  { icon: HardHat, title: "Safety Above Schedule", desc: "ISO 45001-aligned HSE systems across every site. 1.1 million safe man-hours on our latest tower alone." },
];

const processSteps = [
  { n: "01", title: "Consult & Survey", desc: "Site study, soil investigation and requirement mapping with our engineers." },
  { n: "02", title: "Design & BOQ", desc: "Architectural design, 3D visualisation and a measurable, itemised BOQ." },
  { n: "03", title: "Contract & Advance", desc: "Transparent contract with milestone schedule. 10–20% advance mobilises your site." },
  { n: "04", title: "Build & Verify", desc: "Daily progress reports, weekly dashboards and joint milestone certification." },
  { n: "05", title: "Handover & Care", desc: "Snag-free handover with documentation, warranties and defect liability support." },
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then(({ data }) => setProjects(data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data.slice(0, 3))).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="OGK flagship construction" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/70 to-carbon/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-20 pt-40">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Overline className="mb-6">Construction · Engineering · Infrastructure</Overline>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter text-white leading-[0.95] max-w-4xl">
              We Build The<br />Foundations Of <span className="text-ember">Tomorrow</span>
            </h1>
            <p className="text-silver text-base sm:text-lg max-w-xl mt-8 leading-relaxed">
              For 18 years, OGK Infra & Developers has delivered residential towers, commercial campuses, industrial facilities and public infrastructure — on time, on budget, and to specifications that outlast contracts.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/request-quote" data-testid="hero-quote-btn"
                className="group bg-ember text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 flex items-center gap-2 hover:bg-[#e64a19] transition-colors duration-200">
                Request a Quote <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/projects" data-testid="hero-projects-btn"
                className="border border-white/30 text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 hover:border-ember hover:text-ember transition-colors duration-200">
                Explore Projects
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 border border-white/15 mt-20 backdrop-blur-sm bg-black/30" data-testid="hero-stats">
            {[
              { end: 250, suffix: "+", label: "Projects Delivered" },
              { end: 18, suffix: "", label: "Years of Excellence" },
              { end: 4.5, suffix: "M", decimals: 1, label: "Sq.Ft Constructed" },
              { end: 98, suffix: "%", label: "On-Time Completion" },
            ].map((s, i) => (
              <div key={s.label} className={`p-6 sm:p-8 ${i !== 0 ? "border-l border-white/15" : ""} ${i >= 2 ? "border-t md:border-t-0" : ""}`}>
                <p className="font-heading text-3xl sm:text-4xl text-white font-semibold">
                  <CountUp end={s.end} suffix={s.suffix} decimals={s.decimals || 0} />
                </p>
                <p className="text-silver text-xs uppercase tracking-[0.18em] mt-2">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-line bg-graphite py-5" data-testid="services-marquee">
        <Marquee gradient={false} speed={40} autoFill>
          {["Residential", "Commercial", "Industrial", "Government", "Roads & Infrastructure", "Renovation", "Interiors", "Architecture", "Structural Design", "BOQ & Surveying", "Project Management", "Material Supply", "Turnkey"].map((s) => (
            <span key={s} className="mx-8 font-heading uppercase tracking-[0.2em] text-sm text-silver flex items-center gap-8">
              {s} <span className="w-1.5 h-1.5 bg-ember inline-block" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* OVERVIEW */}
      <section className="py-24 sm:py-32" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <div className="img-zoom overflow-hidden border border-line">
              <img src={OVERVIEW_IMG} alt="OGK engineers at work" className="w-full h-[480px] object-cover" />
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <Overline className="mb-4">Who We Are</Overline>
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-white leading-[1.05]">
              An Engineering-First Construction Company
            </h2>
            <p className="text-silver mt-6 leading-relaxed">
              OGK Infra & Developers is a full-service construction and infrastructure company headquartered in Imphal, Manipur. We unite architects, structural engineers, quantity surveyors and site teams under one roof — so your project has one accountable partner from the first sketch to the final key.
            </p>
            <p className="text-silver mt-4 leading-relaxed">
              Our clients award us projects through transparent quotations and contracts, pay verified milestone bills through our client portal, and track every rupee from advance to final settlement.
            </p>
            <div className="grid grid-cols-2 gap-px bg-line border border-line mt-10">
              {[["1,200+", "Skilled Workforce"], ["12", "Cities Served"], ["₹850 Cr+", "Work Executed"], ["0", "Arbitration Disputes"]].map(([v, l]) => (
                <div key={l} className="bg-carbon p-6">
                  <p className="font-heading text-2xl text-ember font-semibold">{v}</p>
                  <p className="text-silver text-xs uppercase tracking-[0.15em] mt-1">{l}</p>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="overview-about-link" className="inline-flex items-center gap-2 mt-10 text-white uppercase text-xs tracking-[0.2em] font-bold border-b-2 border-ember pb-2 hover:text-ember transition-colors duration-200">
              The OGK Story <ArrowRight size={14} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="py-24 sm:py-32 bg-graphite border-y border-line" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Core Services" title="Thirteen Disciplines. One Standard." sub="Every service is delivered by dedicated in-house teams — never subcontracted accountability." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {coreServices.map((s, i) => (
              <FadeIn key={s.slug} delay={i * 0.05}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group bg-graphite p-8 h-full flex flex-col hover:bg-carbon transition-colors duration-300">
                  <s.icon size={26} className="text-ember mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-white uppercase tracking-tight leading-tight">{s.title}</h3>
                  <p className="text-silver text-sm mt-3 leading-relaxed flex-1">{s.desc}</p>
                  <span className="mt-6 text-xs uppercase tracking-[0.2em] text-silver group-hover:text-ember flex items-center gap-2 transition-colors duration-200">
                    Explore <ArrowUpRight size={13} />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="all-services-link" className="inline-flex items-center gap-2 text-white uppercase text-xs tracking-[0.2em] font-bold border-b-2 border-ember pb-2 hover:text-ember transition-colors duration-200">
              View All 13 Services <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 sm:py-32" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Why OGK" title="Trust Is Our Primary Building Material" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {whyUs.map((w, i) => (
              <FadeIn key={w.title} delay={i * 0.08}>
                <div className="border-t-2 border-ember pt-8">
                  <w.icon size={28} className="text-white mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-white uppercase tracking-tight">{w.title}</h3>
                  <p className="text-silver text-sm mt-4 leading-relaxed">{w.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-24 sm:py-32 bg-graphite border-y border-line" data-testid="featured-projects-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Featured Work" title="Structures That Speak For Us" />
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((p, i) => (
              <FadeIn key={p.slug} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
                <Link to={`/projects/${p.slug}`} data-testid={`featured-project-${p.slug}`}
                  className="group block img-zoom relative overflow-hidden border border-line">
                  <img src={p.cover_image} alt={p.title} className={`w-full object-cover ${i === 0 ? "h-[420px] sm:h-[520px]" : "h-[320px]"}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <StatusBadge status={p.status} />
                      <span className="text-silver text-xs uppercase tracking-[0.15em]">{p.category} · {p.location}</span>
                    </div>
                    <h3 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight">{p.title}</h3>
                    {p.status === "Ongoing" && (
                      <div className="mt-4 max-w-xs">
                        <div className="flex justify-between text-xs text-silver mb-1"><span>Progress</span><span className="text-ember font-bold">{p.completion_pct}%</span></div>
                        <div className="h-1 bg-white/15"><div className="h-1 bg-ember" style={{ width: `${p.completion_pct}%` }} /></div>
                      </div>
                    )}
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/projects" data-testid="all-projects-link" className="inline-flex items-center gap-2 text-white uppercase text-xs tracking-[0.2em] font-bold border-b-2 border-ember pb-2 hover:text-ember transition-colors duration-200">
              View Full Portfolio <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 sm:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="How We Work" title="From First Meeting To Final Key" sub="A disciplined five-stage delivery process, with milestone-based payments so you always pay for verified progress." />
          <div className="grid md:grid-cols-5 gap-px bg-line border border-line">
            {processSteps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.07}>
                <div className="bg-carbon p-8 h-full">
                  <p className="font-heading text-4xl text-ember/80 font-semibold">{s.n}</p>
                  <h3 className="font-heading text-base text-white uppercase tracking-tight mt-6">{s.title}</h3>
                  <p className="text-silver text-sm mt-3 leading-relaxed">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32 bg-graphite border-y border-line" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Client Voices" title="What Project Owners Say" />
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <FadeIn key={t.id || i} delay={i * 0.08}>
                <div className="border border-line bg-carbon p-8 h-full flex flex-col lift" data-testid={`testimonial-card-${i}`}>
                  <Quote size={24} className="text-ember mb-6" />
                  <p className="text-white/90 leading-relaxed text-sm flex-1">"{t.quote}"</p>
                  <div className="mt-8 pt-6 border-t border-line">
                    <p className="font-heading text-white uppercase tracking-wide text-sm">{t.name}</p>
                    <p className="text-silver text-xs mt-1 uppercase tracking-[0.15em]">{t.role} — {t.company}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="py-24 sm:py-32" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Insights" title="From The Field & The Drawing Board" />
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((b, i) => (
              <FadeIn key={b.slug} delay={i * 0.08}>
                <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block border border-line bg-graphite lift h-full">
                  <div className="img-zoom overflow-hidden h-52"><img src={b.image} alt={b.title} className="w-full h-full object-cover" /></div>
                  <div className="p-7">
                    <p className="text-ember text-xs uppercase tracking-[0.2em] font-bold">{b.category}</p>
                    <h3 className="font-heading text-lg text-white mt-3 leading-snug group-hover:text-ember transition-colors duration-200">{b.title}</h3>
                    <p className="text-silver text-sm mt-3 leading-relaxed line-clamp-2">{b.excerpt}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-line bg-ember" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div>
            <h2 className="font-heading text-3xl sm:text-5xl uppercase tracking-tighter text-white leading-[1]">Have A Project<br />In Mind?</h2>
            <p className="text-white/85 mt-4 max-w-lg">Get a detailed, itemised quotation within 72 hours — or book a free site visit with our engineers.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/request-quote" data-testid="cta-quote-btn" className="bg-carbon text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 hover:bg-black transition-colors duration-200">Request a Quote</Link>
            <Link to="/book-site-visit" data-testid="cta-visit-btn" className="border-2 border-white text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 hover:bg-white hover:text-ember transition-colors duration-200">Book Site Visit</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
